import { css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { Tooltip, type TooltipProps } from "@mui/material";
import type { ComponentProps, PropsWithChildren, ReactNode } from "react";

import { shade } from "@skilitics/designer-theme";

const float = keyframes`
  from {
    transform: rotateY(0deg);
    opacity: 1;
  }
  to {
    transform: rotateY(360deg);
    opacity: 0.5;
  }
`;

const Button = styled.button<{ active?: boolean; busy?: boolean; disabled?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;

  height: 36px;
  width: 36px;

  color: ${({ theme }) => theme.generalTextIcon};
  background-color: ${({ theme, active }) => (active ? theme.surfaceVariant : theme.surface)};

  font-size: 18px;

  border-color: transparent;

  pointer-events: ${({ busy, disabled }) => (busy || disabled ? "none" : "revert")};

  opacity: ${({ disabled }) => (disabled ? 0.25 : 1)};

  cursor: pointer;

  transition:
    opacity 300ms ease-in-out,
    transform 300ms ease-in-out,
    background-color 300ms ease-in-out;

  ${({ busy }) =>
    busy &&
    css`
      animation: ${float} 1500ms ease-in-out infinite alternate;
    `};

  &:hover {
    background-color: ${({ theme, active }) => !active && shade(theme.surface, 0.04)};
  }

  &:focus {
    outline: none;
  }

  &:active {
    background-color: ${({ theme, active }) => !active && shade(theme.surface, 0.1)};
  }

  &:disabled {
    color: ${({ theme }) => theme.generalTextIconVariant};

    cursor: default;
  }
`;

type Props = ComponentProps<"button"> & {
  active?: boolean;
  busy?: boolean;
  disabled?: boolean;
  placement?: TooltipProps["placement"];
  tooltip?: ReactNode;
  ["data-test"]: string; // Required for testing purposes.
};

export const ToolBarButton = ({ tooltip, placement = "top", children, ...props }: PropsWithChildren<Props>) => (
  <Tooltip title={tooltip} placement={placement}>
    <span>
      <Button {...props}>{children}</Button>
    </span>
  </Tooltip>
);
